import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Skeleton, Button } from '@mui/material';
import { getSteamGameDetails, getSteamReviews } from '../../services/SteamApi';
import type { SteamGameDetails, SteamReviews } from '../../services/SteamApi';
import LanguageIcon from '@mui/icons-material/Language';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ComputerIcon from '@mui/icons-material/Computer';

interface GameCompactInfoProps {
  appId: string;
  xboxId?: string;
  psId?: string;
  currentPlayers?: number;
}

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    minimumFractionDigits: 0
  }).format(price / 100);
};

// MetacriticスコアのカラーマッピングをMetacriticと同様の表現に
const getMetacriticColor = (score: number): string => {
  if (score >= 90) return '#00ce7a'; // Universal acclaim
  if (score >= 75) return '#66cc33'; // Generally favorable
  if (score >= 50) return '#ffcc33'; // Mixed or average
  if (score >= 20) return '#ff0000'; // Generally unfavorable
  return '#ff0000'; // Overwhelming dislike
};

const GameCompactInfo: React.FC<GameCompactInfoProps> = ({ appId, xboxId, psId, currentPlayers }) => {
  const [gameDetails, setGameDetails] = useState<SteamGameDetails | null>(null);
  const [reviewDetails, setReviewDetails] = useState<SteamReviews | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [details, reviews] = await Promise.all([
          getSteamGameDetails(appId),
          getSteamReviews(appId)
        ]);
        setGameDetails(details);
        // グローバルレビューのみ使用
        setReviewDetails(reviews.global);
      } catch (error) {
        console.error('Steamの情報取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [appId]);

  if (loading) {
    return (
      <Box sx={{ 
        p: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 1,
        border: '1px solid rgba(255, 255, 255, 0.05)'
      }}>
        <Skeleton variant="rectangular" width="100%" height={150} />
        <Skeleton variant="text" sx={{ mt: 1 }} />
        <Skeleton variant="text" />
        <Skeleton variant="text" width="60%" />
      </Box>
    );
  }

  if (!gameDetails) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        borderRadius: 1,
        border: "1px solid rgba(255, 255, 255, 0.05)",
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={gameDetails.header_image}
        alt={gameDetails.name}
        sx={{
          width: "100%",
          height: "auto",
          display: "block",
        }}
      />

      <Box sx={{ p: 1.5 }}>
        {/* <Typography 
          variant="subtitle1" 
          sx={{ 
            fontSize: '0.8rem',
            fontWeight: 500,
            mb: 0.5
          }}
        >
          {gameDetails.name}
        </Typography> */}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: "0.65rem",
            mb: 1.5,
            lineHeight: "1.25",
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {gameDetails.short_description}
        </Typography>

        {gameDetails.release_date && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.65rem",
                color: gameDetails.release_date.coming_soon
                  ? "secondary.main"
                  : "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                lineHeight: "1",
              }}
            >
              {gameDetails.release_date.coming_soon ? "発売予定" : "リリース"}:
              <Box component="span" sx={{ color: "#ffffffe8" }}>
                {gameDetails.release_date.date}
              </Box>
            </Typography>
          </Box>
        )}

        {reviewDetails && !gameDetails.release_date?.coming_soon && reviewDetails.total_reviews > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.65rem",
                lineHeight: "1",
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#ffffffe8",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                                          <img
              src="/icons/service/steam.svg"
              alt="Steam"
              style={{
                width: 16,
                height: 16,
                opacity: 0.85,
              }}
            />

                <Box
                  component="span"
                  sx={{
                    color:
                      reviewDetails.review_score >= 6
                        ? "#66BB6A"
                        : reviewDetails.review_score >= 4
                        ? "#FFA726"
                        : "#EF5350",
                  }}
                >
                  {{
                    "Overwhelmingly Positive": "圧倒的に好評",
                    "Very Positive": "非常に好評",
                    "Positive": "好評",
                    "Mostly Positive": "やや好評",
                    "Mixed": "賛否両論",
                    "Mostly Negative": "やや不評",
                    "Negative": "不評",
                    "Very Negative": "非常に不評",
                    "Overwhelmingly Negative": "圧倒的に不評",
                  }[reviewDetails.review_score_desc] ||
                    reviewDetails.review_score_desc}
                </Box>
                <Typography
                variant="body2"
                sx={{
                  fontSize: "0.65rem",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.25,
                }}
              >-
                <ThumbUpIcon 
                  sx={{
                    fontSize: "0.65rem"
                  }} />
                <Box component="span" sx={{ color: "#ffffffe8" }}>
                  {Math.round(
                    (reviewDetails.total_positive /
                      reviewDetails.total_reviews) *
                      100
                  )}
                  %
                </Box>
                が評価
              </Typography>
                (
                {new Intl.NumberFormat("ja-JP").format(
                  reviewDetails.total_reviews
                )}
                件)
              </Box>
            </Typography>
          </Box>
        )}

        {/* Metacriticスコア表示の追加 */}
        {/* {gameDetails.metacritic && gameDetails.metacritic.score > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.65rem",
                lineHeight: "1",
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#ffffffe8",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  component="a"
                  href={gameDetails.metacritic.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                    gap: 0.5,
                  }}
                >
                  <img
                    src="/icons/service/metacritic.svg"
                    alt="Metacritic"
                    style={{
                      width: 16,
                      height: 16,
                      opacity: 0.7,
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      color: getMetacriticColor(gameDetails.metacritic.score),
                      fontWeight: 600,
                    }}
                  >
                    {gameDetails.metacritic.score}
                  </Box>
                </Box>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  / Metascore
                </Box>
              </Box>
            </Typography>
          </Box>
        )} */}

        {/* オンライン人数表示 */}
        {currentPlayers !== undefined && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.65rem",
                lineHeight: "1",
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#ffffffe8",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <ComputerIcon 
                  sx={{ 
                    fontSize: 16, 
                    opacity: 0.85,
                    color: '#ffffffe8'
                  }} 
                />
                <Box component="span" sx={{ color: "primary.main" }}>
                  {new Intl.NumberFormat("ja-JP").format(currentPlayers)}人
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.65rem",
                    color: "text.secondary",
                    lineHeight: "1",
                  }}
                >
                  がプレイ中
                </Typography>
              </Box>
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: "auto" }}>
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
            }}
          >
            <img
              src="/icons/service/steam.svg"
              alt="Steam"
              style={{
                width: 16,
                height: 16,
                opacity: 0.7,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.65rem",
                color: "#a7a7a7",
                letterSpacing: "0.02em",
              }}
            >
              販売価格
            </Typography>
          </Box> */}
          {gameDetails.is_free ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", color: "#ffffffe8" }}
            >
              無料プレイ
            </Typography>
          ) : gameDetails.price_overview ? (
            <Box>
              {gameDetails.price_overview.discount_percent > 0 && (
                <Typography
                  component="span"
                  sx={{
                    bgcolor: "rgba(190, 238, 17, 0.2)",
                    color: "#BEEE11",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    mr: 1,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                >
                  -{gameDetails.price_overview.discount_percent}%
                </Typography>
              )}
              <Typography
                variant="subtitle1"
                component="span"
                sx={{
                  color:
                    gameDetails.price_overview.discount_percent > 0
                      ? "#BEEE11"
                      : "#ffffffe8",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                {formatPrice(gameDetails.price_overview.final)}
              </Typography>
              {gameDetails.price_overview.discount_percent > 0 && (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    ml: 1,
                    textDecoration: "line-through",
                    color: "text.secondary",
                    fontSize: "0.7rem",
                  }}
                >
                  {formatPrice(gameDetails.price_overview.initial)}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              価格情報なし
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 0.75,
            "& .MuiButton-root": {
              minWidth: "auto",
              borderRadius: "6px",
              transition: "all 0.2s ease",
              backgroundColor: "rgba(255, 255, 255, 0.06)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textTransform: "none",
              padding: "2px 12px",
              color: "text.secondary",
              lineHeight: "1",
              fontSize: "0.65rem",
              fontWeight: 500,
              letterSpacing: "0.02em",
              "& .MuiButton-startIcon": {
                margin: 0,
                paddingRight: "8px",
                "& > *:nth-of-type(1)": {
                  fontSize: 20,
                },
              },
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                transform: "translateY(-1px)",
                "& img": {
                  opacity: 1,
                },
              },
            },
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={
              <img
                src="/icons/service/steam.svg"
                alt="Steam"
                style={{
                  width: 20,
                  height: 20,
                  opacity: 0.9,
                }}
              />
            }
            component="a"
            href={`https://store.steampowered.com/app/${appId}/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Steam
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={
              <img
                src="/icons/service/amazon.svg"
                alt="Amazon"
                style={{
                  width: 20,
                  height: 20,
                  filter:
                    "brightness(0) saturate(100%) invert(71%) sepia(86%) saturate(1857%) hue-rotate(360deg) brightness(103%) contrast(106%)",
                  opacity: 0.9,
                }}
              />
            }
            component="a"
            href={`https://www.amazon.co.jp/s?k=${encodeURIComponent(gameDetails.name)}&tag=pulse-db-22`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Amazon
          </Button>
          {psId && (
            <Button
              variant="contained"
              size="small"
              startIcon={
                <img
                  src="/icons/service/playstation_color.svg"
                  alt="PlayStation"
                  style={{ width: 20, height: 20, opacity: 1 }}
                />
              }
              component="a"
              href={`https://store.playstation.com/ja-jp/concept/${psId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              PlayStation
            </Button>
          )}
          {xboxId && (
            <Button
              variant="contained"
              size="small"
              startIcon={
                <img
                  src="/icons/service/xbox_color.svg"
                  alt="Xbox"
                  style={{ width: 18, height: 18, opacity: 1 }}
                />
              }
              component="a"
              href={`https://www.microsoft.com/ja-jp/p/product/${xboxId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Xbox
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={
              <img
                src="/icons/service/rakuten.svg"
                alt="Rakuten"
                style={{
                  width: 20,
                  height: 20,
                  opacity: 1,
                }}
              />
            }
            component="a"
            href={`https://hb.afl.rakuten.co.jp/hgc/105f4abe.0bb38550.105f4abf.cdf46e34/?pc=https%3A%2F%2Fsearch.rakuten.co.jp%2Fsearch%2Fmall%2F${encodeURIComponent(
              gameDetails.name
            )}%2F101205%2F&link_type=hybrid_url&ut=eyJwYWdlIjoidXJsIiwidHlwZSI6Imh5YnJpZF91cmwiLCJjb2wiOjF9`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Rakuten
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={
              <img 
                src="/icons/service/mercari.svg" 
                alt="Mercari" 
                style={{ 
                  width: 20,
                  height: 20,
                  padding: 1,
                  opacity: 0.8 
                }} 
              />
            }
            component="a"
            href={`https://jp.mercari.com/search?afid=7418318322&category_id=76&keyword=${encodeURIComponent(gameDetails.name)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Mercari
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default GameCompactInfo; 